import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowRight, Loader2 } from "lucide-react";
import { useAuth, formatApiError } from "@/lib/auth";
import { userCan } from "@/lib/rbac";
import { Logo } from "@/components/Brand";
import { useCms } from "@/lib/cms";

export default function Login() {
  const { site } = useCms();
  const brandName = site?.company_name || "";
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password, remember);
      toast.success(`Welcome, ${u.name}`);
      const goAdmin = userCan(u, "reports:view") || userCan(u, "teachers:write") || userCan(u, "bookings:write") || userCan(u, "certificates:write") || userCan(u, "cms:write");
      nav(goAdmin ? "/admin" : "/portal");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white">
      <div className="hidden md:flex flex-col justify-between p-10 bg-[#0F172A] text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(79,70,229,0.35),transparent_55%),radial-gradient(ellipse_at_bottom_left,rgba(16,185,129,0.22),transparent_55%)]" />
        <div className="absolute inset-0 dot-bg opacity-30" />
        <Logo className="relative text-white [&_span]:!text-white" />
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }} className="relative">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-400 mb-4">Enterprise dashboard</div>
          <h2 className="font-display text-4xl tracking-tight font-semibold leading-tight">Every training, every teacher, every certificate — in one calm workspace.</h2>
          <div className="mt-8 text-sm text-gray-400">Trusted by 200+ schools.</div>
        </motion.div>
        <div className="relative text-xs text-gray-400">© {brandName} Institute</div>
      </div>
      <div className="flex items-center justify-center p-8">
        <motion.form initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} onSubmit={submit} autoComplete="off" className="w-full max-w-sm">
          <h1 className="font-display text-3xl tracking-tight font-semibold">Welcome back</h1>
          <p className="text-sm text-gray-500 mt-1">Sign in to your workspace.</p>

          <div className="mt-6 space-y-3">
            <input required type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" autoComplete="off" name="login-email" className="w-full px-3 py-3 rounded-lg border border-gray-200 focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10 outline-none text-sm transition-all" />

            <div className="relative">
              <input
                required
                type={show ? "text" : "password"}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                name="login-password"
                data-testid="login-password"
                className="w-full pr-10 px-3 py-3 rounded-lg border border-gray-200 focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10 outline-none text-sm transition-all"
              />
              <button
                type="button"
                onClick={() => setShow((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 grid place-items-center text-gray-400 hover:text-gray-700"
                aria-label={show ? "Hide password" : "Show password"}
                data-testid="login-toggle-password"
              >
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            <div className="flex items-center justify-between text-xs">
              <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  data-testid="login-remember"
                  className="w-4 h-4 rounded border-gray-300 text-[#4F46E5] focus:ring-[#4F46E5]"
                />
                <span className="text-gray-600">Remember me</span>
              </label>
              <Link to="/forgot-password" data-testid="login-forgot" className="text-[#4F46E5] font-medium hover:underline">
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#0F172A] disabled:bg-gray-400 hover:bg-[#1e293b] text-white text-sm font-medium px-4 py-3 rounded-lg transition-colors"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </div>

        </motion.form>
      </div>
    </div>
  );
}
